import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, CheckCircle, Camera, MapPin, Clock, Target } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/Layout';
import QRCodeScanner from '../../components/qr/QRCodeScanner/QRCodeScanner';
import './QRScanner.css';
import { scanQRCode } from '../../services/api';
import apiClient from '../../services/api';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const QRScanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, role } = useAuth();
  
  // Existing states
  const [scanResult, setScanResult] = useState(null);
  const [binData, setBinData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // New task verification states
  const [taskVerificationMode, setTaskVerificationMode] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [verificationStep, setVerificationStep] = useState('qr_scan'); // qr_scan, location_verify, photo_before, photo_after
  const [locationVerified, setLocationVerified] = useState(false);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [taskDeadline, setTaskDeadline] = useState(null);
  
  // Check authentication and role on component mount
  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to access QR Scanner');
      localStorage.setItem('redirectAfterLogin', '/qr-scanner');
      navigate('/signin');
      return;
    }
    
    // Check if user has appropriate role
    if (role !== 'user' && role !== 'worker' && role !== 'volunteer') {
      toast.error('Only User, Worker & Volunteer can access this feature');
      navigate('/');
      return;
    }
  }, [isAuthenticated, role, navigate]);
  
  // Check if we're in task verification mode from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const taskId = urlParams.get('taskId');
    
    if (taskId) {
      setTaskVerificationMode(true);
      fetchTaskDetails(taskId);
    }
  }, [location]);
  
  // Fetch task details for verification mode
  const fetchTaskDetails = async (taskId) => {
    try {
      const token = localStorage.getItem('binbuddy_token');
      const response = await axios.get(`/api/tasks-enhanced/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentTask(response.data.task);
      setTaskDeadline(new Date(response.data.task.deadline));
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Failed to load task details');
      setTaskVerificationMode(false);
    }
  };
  
  // Handle task verification QR scan
  const handleTaskVerificationScan = async (result) => {
    if (!currentTask) return;
    
    try {
      // Extract bin ID from QR code
      let binId = null;
      
      if (result.includes('/report-bin/')) {
        const urlParts = result.split('/');
        binId = urlParts[urlParts.length - 1];
      } else if (result.startsWith('BIN')) {
        binId = result;
      } else {
        const binIdMatch = result.match(/BIN\d+/i);
        if (binIdMatch) {
          binId = binIdMatch[0].toUpperCase();
        }
      }
      
      // Verify this is the correct bin for the task
      const taskBinId = currentTask.bins[0]?.binId || currentTask.bins[0]?._id;
      
      if (binId !== taskBinId) {
        toast.error(`Wrong bin! This task is for bin ${taskBinId}, but you scanned ${binId}`);
        return;
      }
      
      // QR verification successful
      toast.success('QR Code verified! Proceeding to location verification...');
      setVerificationStep('location_verify');
      
      // Update task with QR verification
      const token = localStorage.getItem('binbuddy_token');
      await axios.patch(`/api/tasks-enhanced/${currentTask._id}/verify-qr`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
    } catch (error) {
      console.error('Error in task verification:', error);
      toast.error('QR verification failed');
    }
  };
  
  // Handle location verification
  const handleLocationVerification = async () => {
    try {
      setIsLoading(true);
      const location = await getUserLocation();
      
      const token = localStorage.getItem('binbuddy_token');
      const response = await axios.post(`/api/tasks-enhanced/${currentTask._id}/verify-location`, {
        workerLat: location.latitude,
        workerLng: location.longitude
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.locationVerified) {
        setLocationVerified(true);
        setVerificationStep('photo_before');
        toast.success(`Location verified! Distance: ${response.data.distance}m`);
      } else {
        toast.error(`Location verification failed. You are ${response.data.distance}m away from the bin. Please get closer.`);
      }
      
    } catch (error) {
      console.error('Error verifying location:', error);
      toast.error('Location verification failed');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle photo capture for task verification
  const handleTaskPhotoCapture = async (photoType) => {
    if (!videoRef.current || !canvasRef.current) return;
    
    try {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);
      
      // Convert to blob
      canvasRef.current.toBlob(async (blob) => {
        const formData = new FormData();
        formData.append('photo', blob, `${photoType}-${Date.now()}.jpg`);
        formData.append('photoType', photoType);
        
        try {
          const token = localStorage.getItem('binbuddy_token');
          const response = await axios.post(`/api/tasks-enhanced/${currentTask._id}/upload-photo`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (photoType === 'before') {
            setBeforePhoto(response.data.photoUrl);
            toast.success('Before photo captured! Now complete your work and take an after photo.');
            setVerificationStep('photo_after');
          } else {
            setAfterPhoto(response.data.photoUrl);
            toast.success('After photo captured! Completing task...');
            await completeTask();
          }
          
          handleCameraClose();
          
        } catch (error) {
          console.error('Error uploading photo:', error);
          toast.error('Failed to upload photo');
        }
      }, 'image/jpeg', 0.8);
      
    } catch (error) {
      console.error('Error capturing photo:', error);
      toast.error('Failed to capture photo');
    }
  };
  
  // Complete the task
  const completeTask = async () => {
    try {
      const token = localStorage.getItem('binbuddy_token');
      await axios.patch(`/api/tasks-enhanced/${currentTask._id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Task completed successfully! Awaiting admin review.');
      navigate('/dashboard/worker');
      
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };
  
  const handleScan = async (result) => {
    if (!result) {
      setError('Invalid QR code scanned');
      return;
    }
    
    setIsLoading(true);
    setScanResult(result);
    setError(null);

    try {
      console.log('🔍 Scanned QR result:', result);
      console.log('🔍 QR result type:', typeof result);
      
      // If in task verification mode, handle differently
      if (taskVerificationMode && verificationStep === 'qr_scan') {
        await handleTaskVerificationScan(result);
        setIsLoading(false);
        return;
      }
      
      // Handle different QR code formats
      let binId = null;
      
      if (result.includes('/report-bin/')) {
        // New format: extract bin ID from report-bin URL and redirect to report page
        const urlParts = result.split('/');
        binId = urlParts[urlParts.length - 1].trim();
        
        if (!binId) {
          throw new Error('Could not extract bin ID from QR code');
        }
        
        console.log('✅ New format QR detected - Bin ID:', binId);
        
        // Show loading toast
        const loadingToast = toast.loading(`Loading bin ${binId}...`);
        
        // Small delay to ensure state is set
        setTimeout(() => {
          toast.dismiss(loadingToast);
          toast.success(`Redirecting to report page`);
          navigate(`/report-bin/${binId}`);
        }, 300);
        return;
      } else if (result.includes('/uploads/qr-codes/')) {
        // Old format: extract bin ID from file path
        const filename = result.split('/').pop();
        binId = filename.replace(/\.(png|jpg|jpeg)$/i, '');
        console.log('⚠️ Old format QR detected - Bin ID:', binId);
      } else if (result.startsWith('BIN')) {
        // Direct bin ID format
        binId = result;
        console.log('📋 Direct bin ID format - Bin ID:', binId);
      } else {
        // Try to extract bin ID from any format
        const binIdMatch = result.match(/BIN\d+/i);
        if (binIdMatch) {
          binId = binIdMatch[0].toUpperCase();
          console.log('🔍 Extracted bin ID from text:', binId);
        }
      }

      if (!binId) {
        throw new Error('Could not extract bin ID from QR code. Please ensure you are scanning a valid BinBuddy QR code.');
      }

      // Use the public endpoint to get bin data
      console.log('🌐 Fetching bin data for:', binId);
      const response = await apiClient.get(`/bins/public/${binId}`);
      setBinData(response.data.data);
      setShowStatusForm(true);
      toast.success(`Bin ${binId} data retrieved successfully!`);
    } catch (err) {
      console.error('Error processing QR scan:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process QR code';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  };

  const handleCameraOpen = () => {
    setCameraOpen(true);
    navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      } 
    })
      .then(stream => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(stream => {
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
            }
          })
          .catch(err => {
            console.error("Error accessing camera: ", err);
            toast.error('Could not access camera. Please ensure you have granted permission.');
            setCameraOpen(false);
          });
      });
  };

  const handleCameraClose = () => {
    setCameraOpen(false);
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const handleCapture = () => {
    if (taskVerificationMode && (verificationStep === 'photo_before' || verificationStep === 'photo_after')) {
      // Handle task verification photo capture
      const photoType = verificationStep === 'photo_before' ? 'before' : 'after';
      handleTaskPhotoCapture(photoType);
      return;
    }
    
    // Original photo capture logic for status updates
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0, videoRef.current.videoWidth, videoRef.current.videoHeight);
      
      canvasRef.current.toBlob(blob => {
        const capturedFile = new File([blob], `bin-status-${Date.now()}.jpg`, { type: "image/jpeg" });
        setCapturedPhoto(capturedFile);
        setPhotoPreview(URL.createObjectURL(capturedFile));
        handleCameraClose();
        toast.success('Photo captured successfully!');
      }, 'image/jpeg', 0.8);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus || !capturedPhoto) {
      toast.error('Please select a status and capture a photo');
      return;
    }

    setSubmitting(true);
    
    try {
      // Get user's current location
      const location = await getUserLocation();
      setUserLocation(location);

      // Create FormData
      const formData = new FormData();
      formData.append('binId', binData.binId);
      formData.append('status', selectedStatus.toLowerCase());
      formData.append('userLatitude', location.latitude);
      formData.append('userLongitude', location.longitude);
      formData.append('photo', capturedPhoto);

      // Submit status update
      await apiClient.post('/bins/update-status', formData);
      
      toast.success('Bin status updated successfully!');
      
      // Reset form
      setShowStatusForm(false);
      setSelectedStatus('');
      setCapturedPhoto(null);
      setPhotoPreview('');
      setBinData(null);
      setScanResult(null);
      
    } catch (err) {
      console.error('Error updating bin status:', err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Failed to update bin status');
      }
    } finally {
      setSubmitting(false);
    }
  };

  
  const resetScan = () => {
    setScanResult(null);
    setBinData(null);
    setError(null);
  };

  return (
    <Layout title={taskVerificationMode ? "Task Verification" : "QR Code Scanner"}>
      <div className="qr-scanner-container">
        {/* Task Verification Mode UI */}
        {taskVerificationMode && currentTask && (
          <div className="task-verification-header">
            <div className="task-info-card">
              <div className="task-header">
                <h2>Task Verification</h2>
                <div className="task-deadline">
                  <Clock size={16} />
                  <span>Deadline: {taskDeadline ? taskDeadline.toLocaleTimeString() : 'Loading...'}</span>
                </div>
              </div>
              <div className="task-details">
                <p><strong>Issue:</strong> {currentTask.reportId?.issueType?.replace('_', ' ') || 'Task completion'}</p>
                <p><strong>Bin:</strong> {currentTask.bins[0]?.binId || currentTask.bins[0]?._id}</p>
                <p><strong>Location:</strong> {currentTask.bins[0]?.address || 'Bin location'}</p>
              </div>
              
              {/* Verification Steps Progress */}
              <div className="verification-steps">
                <div className={`step ${verificationStep === 'qr_scan' ? 'active' : verificationStep !== 'qr_scan' ? 'completed' : ''}`}>
                  <div className="step-number">1</div>
                  <span>Scan QR Code</span>
                </div>
                <div className={`step ${verificationStep === 'location_verify' ? 'active' : locationVerified ? 'completed' : ''}`}>
                  <div className="step-number">2</div>
                  <span>Verify Location</span>
                </div>
                <div className={`step ${verificationStep === 'photo_before' ? 'active' : beforePhoto ? 'completed' : ''}`}>
                  <div className="step-number">3</div>
                  <span>Before Photo</span>
                </div>
                <div className={`step ${verificationStep === 'photo_after' ? 'active' : afterPhoto ? 'completed' : ''}`}>
                  <div className="step-number">4</div>
                  <span>After Photo</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="scanner-container">
          <div className="scanner-card">
            <h2 className="scanner-title">
              {taskVerificationMode ? 
                (verificationStep === 'qr_scan' ? 'Scan Bin QR Code' : 
                 verificationStep === 'location_verify' ? 'Verify Location' :
                 verificationStep === 'photo_before' ? 'Take Before Photo' :
                 'Take After Photo') : 
                'Scan Bin QR Code'
              }
            </h2>
            <p className="scanner-desc">
              {taskVerificationMode ? 
                (verificationStep === 'qr_scan' ? 'Scan the QR code of the assigned bin to verify you are at the correct location.' :
                 verificationStep === 'location_verify' ? 'Verify your location is within 10 meters of the bin.' :
                 verificationStep === 'photo_before' ? 'Take a photo of the bin before starting work.' :
                 'Take a photo of the bin after completing the work.') :
                'Point your camera at a bin\'s QR code to view its details and update its status.'
              }
            </p>

            {/* Task Verification Action Buttons */}
            {taskVerificationMode && verificationStep === 'location_verify' && (
              <div className="task-verification-actions">
                <button 
                  onClick={handleLocationVerification}
                  disabled={isLoading}
                  className="verification-btn location-btn"
                >
                  <Target size={20} />
                  {isLoading ? 'Verifying Location...' : 'Verify My Location'}
                </button>
              </div>
            )}

            {taskVerificationMode && (verificationStep === 'photo_before' || verificationStep === 'photo_after') && (
              <div className="task-verification-actions">
                <button 
                  onClick={handleCameraOpen}
                  className="verification-btn camera-btn"
                >
                  <Camera size={20} />
                  {verificationStep === 'photo_before' ? 'Take Before Photo' : 'Take After Photo'}
                </button>
              </div>
            )}

            {scanResult ? (
              <div className="scanner-result">
                {isLoading ? (
                  <div className="loading-section">
                    <div className="loader"></div>
                    <p className="loading-text">Fetching bin data...</p>
                  </div>
                ) : error ? (
                  <div className="error-box">
                    <AlertCircle className="icon" />
                    <div>
                      <p className="error-title">Error retrieving bin data</p>
                      <p>{error}</p>
                      <button onClick={resetScan} className="error-button">Try Again</button>
                    </div>
                  </div>
                ) : binData && showStatusForm ? (
                  <div>
                    <div className="success-box">
                      <CheckCircle className="icon" />
                      <div>
                        <p className="success-title">Bin found!</p>
                        <p>Update status for bin {binData.binId}</p>
                      </div>
                    </div>

                    <div className="bin-details-card">
                      <div className="bin-header">Current Bin Status</div>
                      <div className="bin-body">
                        <div className="detail-grid">
                          <div><span className="label">Bin ID:</span><p>{binData.binId}</p></div>
                          <div><span className="label">Current Status:</span>
                            <p className={`status-text ${binData.status.toLowerCase()}`}>{binData.status}</p>
                          </div>
                          <div><span className="label">Capacity:</span><p>{binData.capacity}L</p></div>
                          <div><span className="label">Location:</span><p>{binData.address?.streetAddress || 'Unknown'}</p></div>
                        </div>

                        <div className="status-update">
                          <h4>Update Bin Status</h4>
                          <div className="status-buttons">
                            {['empty', 'partial', 'full', 'maintenance'].map(status => (
                              <button
                                key={status}
                                onClick={() => setSelectedStatus(status)}
                                className={`status-btn ${status} ${selectedStatus === status ? 'active' : ''}`}
                              >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="photo-capture">
                          <h4>Capture Current Bin Photo</h4>
                          {photoPreview ? (
                            <div className="photo-preview">
                              <img src={photoPreview} alt="Captured bin" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
                              <button onClick={() => { setCapturedPhoto(null); setPhotoPreview(''); }} className="retake-btn">
                                Retake Photo
                              </button>
                            </div>
                          ) : (
                            <button onClick={handleCameraOpen} className="camera-btn">
                              <Camera className="icon" />
                              Capture Photo
                            </button>
                          )}
                        </div>

                        <div className="form-actions">
                          <button 
                            onClick={handleStatusUpdate} 
                            disabled={!selectedStatus || !capturedPhoto || submitting}
                            className="submit-btn"
                          >
                            {submitting ? 'Updating...' : 'Update Status'}
                          </button>
                          <button onClick={resetScan} className="cancel-btn">Cancel</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              // Only show QR scanner when needed
              (!taskVerificationMode || verificationStep === 'qr_scan') && (
                <QRCodeScanner onScan={handleScan} />
              )
            )}
          </div>

          <div className="scan-tips">
            <h3>Tips for Scanning</h3>
            <ul>
              <li>• Ensure the QR code is well-lit and clearly visible</li>
              <li>• Hold your device steady while scanning</li>
              <li>• Make sure the entire QR code is visible in the frame</li>
              <li>• If scanning fails, try adjusting the distance or angle</li>
            </ul>
          </div>
        </div>

        {/* Camera Modal */}
        {cameraOpen && (
          <div className="camera-modal">
            <div className="camera-modal-content">
              <div className="camera-header">
                <h3>Capture Bin Photo</h3>
                <button onClick={handleCameraClose} className="close-btn">×</button>
              </div>
              <div className="camera-body">
                <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxWidth: '400px', borderRadius: '8px' }} />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className="camera-actions">
                <button onClick={handleCapture} className="capture-btn">
                  <Camera className="icon" />
                  Capture
                </button>
                <button onClick={handleCameraClose} className="cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default QRScanner;
